import React from 'react';
import type { ComponentEvent, Page, RestAction } from '../../types/project';
import { getButtonOnClickActionId } from './componentEventUtils';
import styles from './VisualEditor.module.css';

type Props = { events: ComponentEvent[] | undefined; actions: RestAction[]; pages?: Page[]; onChange: (events: ComponentEvent[]) => void };

export default function ButtonEventEditor({ events, actions, pages = [], onChange }: Props): React.ReactElement {
  const actionId = getButtonOnClickActionId(events);
  const navigationId = events?.find(event => event.event === 'onClick')?.navigateToPageId ?? '';
  const selection = navigationId ? `page:${navigationId}` : actionId;
  const update = (value: string) => {
    const other = (events ?? []).filter(event => event.event !== 'onClick');
    if (!value) onChange(other);
    else if (value.startsWith('page:')) onChange([...other, { event: 'onClick', navigateToPageId: value.slice(5) }]);
    else onChange([...other, { event: 'onClick', actionId: value }]);
  };
  const missingReference = actionId !== '' && !actions.some((action) => action.id === actionId);
  return (
    <div data-testid="button-onclick-editor" style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #d8dee4' }}>
      <div className={styles.infoKey} style={{ display: 'block', marginBottom: 6 }}>Events</div>
      <div className={styles.infoRow}>
        <label className={styles.infoKey} htmlFor="prop-onclick-action">On click</label>
        <select id="prop-onclick-action" data-testid="button-onclick-action" className={styles.infoInput}
          value={selection} onChange={(event) => update(event.target.value)}
          style={missingReference ? { borderColor: '#b91c1c' } : undefined}>
          <option value="">No action</option>
          {missingReference && <option value={actionId}>Missing action: {actionId}</option>}
          <optgroup label="Run action">{actions.map((action) => <option key={action.id} value={action.id}>{action.name} ({action.id})</option>)}</optgroup>
          <optgroup label="Navigate to page">{pages.map(page => <option key={page.id} value={`page:${page.id}`}>{page.name}</option>)}</optgroup>
        </select>
      </div>
      {actions.length === 0 && !missingReference && <div style={{ fontSize: 10, color: '#57606a' }}>Create a REST action in Actions &amp; Bindings first.</div>}
      {missingReference && <div role="alert" style={{ fontSize: 10, color: '#b91c1c' }}>
        This onClick event references missing action &quot;{actionId}&quot;. Select a valid action or clear it.
      </div>}
    </div>
  );
}
